import React, { useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { trackInitiateCheckout } from '../utils/trackAddToCart';
import { CheckCircle2, Lock } from 'lucide-react';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-10% 0px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 20
  }} transition={{
    duration: 0.6,
    delay,
    ease: 'easeOut'
  }} data-id="element-10">
      {children}
    </motion.div>;
};
export function AuthoritySection() {
  return <section className="w-full bg-[#050505] py-24 px-4 relative overflow-hidden border-t border-[#111]" data-id="element-11">
      <div className="max-w-2xl mx-auto" data-id="element-12">
        <FadeIn data-id="element-13">
          <div className="space-y-6 text-[#8a8578] text-lg md:text-xl leading-relaxed" data-id="element-14">
            <p className="text-[#f5f0e8] font-serif text-2xl md:text-3xl mb-8" data-id="element-15">
              Esto no salió de teoría.
            </p>

            <p data-id="element-16">
              Salió de repetir el mismo error…
              <br data-id="element-17" />
              hasta que dejó de ser tolerable.
            </p>

            <p data-id="element-18">
              De llevar una cuenta personal de 2K a 17K en real…
              <br data-id="element-19" />
              y destruirla en una sola noche.
              <br data-id="element-20" />
              <span className="text-[#f5f0e8] font-medium" data-id="element-21">
                No por falta de estrategia.
              </span>
              <br data-id="element-22" />
              Por no poder controlarme.
            </p>

            <p data-id="element-23">
              De quemar cuentas de fondeo.
              <br data-id="element-24" />
              Pasar fases… y perderlas.
              <br data-id="element-25" />
              Llegar… y no poder retirar.
              <br data-id="element-26" />
              Durante años.
            </p>

            <p data-id="element-27">
              Entendía el mercado.
              <br data-id="element-28" />
              Sabía lo que hacía.
              <br data-id="element-29" />
              Pero en el momento clave…
              <br data-id="element-30" />
              hacía lo contrario.
            </p>

            <p className="italic text-[#d4af37]" data-id="element-31">
              Y eso… es lo que más desgasta.
              <br data-id="element-32" />
              Porque no puedes culpar a nadie.
            </p>

            <p data-id="element-33">
              Después de 4 años operando…
              <br data-id="element-34" />
              el patrón seguía intacto:
              <br data-id="element-35" />
              <span className="text-[#f5f0e8] font-bold" data-id="element-36">autosabotaje.</span>
            </p>

            <div className="py-8 border-y border-[#222] my-8" data-id="element-37">
              <p className="text-[#f5f0e8] font-medium mb-4" data-id="element-38">
                Hasta que entendí algo incómodo:
              </p>
              <p data-id="element-39">
                No necesitaba más información.
                <br data-id="element-40" />
                Necesitaba un sistema…
                <br data-id="element-41" />
                que funcionara cuando yo no estaba en control.
              </p>
              <p className="text-[#d4af37] font-bold mt-4" data-id="element-42">
                De ahí salió esto.
              </p>
            </div>

            <p data-id="element-43">
              Hoy… opero con capital fondeado de 50K.
              <br data-id="element-44" />Y en los últimos 2 meses… hice 3 retiros consecutivos.
            </p>

            {/* VISUAL PROOF (Real Certificates) */}
            <div className="my-12 space-y-6" data-id="element-45">
              {/* Challenge Passed - 50K */}
              <div className="relative rounded-xl overflow-hidden border border-[#d4af37]/30 shadow-[0_0_30px_rgba(212,175,55,0.1)]" data-id="element-46">
                <img src="https://cdn.magicpatterns.com/uploads/ezsEJ2hocy7cvHD3Qm4ML8/Challenge_Certificate_(1).png" alt="Cuenta Fondeada 50K" className="w-full h-auto" loading="lazy" decoding="async" data-id="element-47" />
                {/* Censor Bar */}
                <div className="absolute top-[50%] left-[4%] w-[45%] h-[12%] bg-[#050505]/95 backdrop-blur-md rounded border border-[#222] flex items-center justify-center shadow-inner" data-id="element-48">
                  <div className="flex items-center gap-1.5 text-[#555]" data-id="element-49">
                    <Lock size={12} data-id="element-50" />
                    <span className="text-[10px] md:text-xs uppercase tracking-widest font-bold" data-id="element-51">
                      Trader Privado
                    </span>
                  </div>
                </div>
              </div>

              {/* Payouts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-52">
                {[{
                src: 'https://cdn.magicpatterns.com/uploads/4NDLmBJmLBdhFAdow9FDHC/Reward_Certificate_(3).png',
                alt: 'Retiro Enero'
              }, {
                src: 'https://cdn.magicpatterns.com/uploads/tEdkn6ZR4mPgLUvAQNe3sN/Reward_Certificate_(4).png',
                alt: 'Retiro Febrero'
              }, {
                src: 'https://cdn.magicpatterns.com/uploads/6gPfMMhg1PkMd499XE6T28/Reward_Certificate_(2).png',
                alt: 'Retiro Marzo'
              }].map((cert, idx) => <div key={idx} className="relative rounded-lg overflow-hidden border border-[#222] shadow-lg hover:border-[#d4af37]/50 transition-colors" data-id="element-53">
                    <img src={cert.src} alt={cert.alt} className="w-full h-auto" loading="lazy" decoding="async" data-id="element-54" />
                    {/* Censor Bar for Payouts */}
                    <div className="absolute top-[50%] left-[4%] w-[45%] h-[13%] bg-[#050505]/95 backdrop-blur-md rounded border border-[#222] flex items-center justify-center" data-id="element-55">
                      <div className="flex items-center gap-1 text-[#444]" data-id="element-56">
                        <Lock size={10} data-id="element-57" />
                        <span className="text-[8px] md:text-[10px] uppercase tracking-widest font-bold" data-id="element-58">
                          Privado
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            <p data-id="element-59">
              No porque ahora "sé más".
              <br data-id="element-60" />
              Sino porque dejé de sabotearme… cuando más importaba.
              <br data-id="element-61" />
              <span className="text-[#f5f0e8] font-medium" data-id="element-62">
                Y eso cambia todo.
              </span>
            </p>

            <p className="pt-6" data-id="element-63">
              Esto existe…
              <br data-id="element-64" />
              porque no quiero que sigas pensando que el problema es el trading.
              <br data-id="element-65" />
              <span className="text-[#f5f0e8] font-bold" data-id="element-66">
                Cuando en realidad… es lo que pasa dentro de ti cuando operas.
              </span>
            </p>
          </div>

          <div className="mt-12 text-center" data-id="element-67">
            <a href="https://pay.hotmart.com/I104339970M?checkoutMode=10" target="_blank" rel="noopener noreferrer" onClick={() => trackInitiateCheckout()} className="inline-block w-full md:w-auto px-12 py-4 bg-gradient-to-r from-[#b8942e] via-[#d4af37] to-[#e8c84a] text-black font-bold text-lg tracking-wide rounded hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all duration-300 transform hover:-translate-y-1" data-id="element-68">
              QUIERO DEJAR DE SABOTEARME HOY
            </a>
          </div>
        </FadeIn>
      </div>
    </section>;
}